import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { crmJobsAPI, customersAPI, jobMaterialsAPI, jobKitsAPI, tagsAPI, jobAttachmentsAPI, projectsAPI, invoicesAPI } from '../lib/api';
import { JobCustomFields } from '../components/JobCustomFields';
import { ApplyWorkflowButton } from '../components/ApplyWorkflowButton';
import { jobTemplatesAPI, predictiveRestockAPI } from '../lib/v2Api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ArrowLeft, Loader2, Edit2, Save, X, Trash2, Briefcase, Building2, Clock, Package, Zap, DollarSign, FileText, FileStack, ExternalLink, ShieldCheck, AlertTriangle, CircleAlert, Truck, RefreshCw, MapPin, Phone, Mail, StickyNote, Plus, Navigation, Bell, CheckCircle2, XCircle, Camera, Upload, Image as ImageIcon, FolderKanban, Receipt, Send, CreditCard, MoreHorizontal, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { formatInTenantTimezone, parseUTC } from '../lib/timezone';
import { JobMaterialsTab } from '../components/JobMaterialsTab';
import { QuickAddMaterialModal } from '../components/QuickAddMaterialModal';
import { JobInvoiceSection } from '../components/JobInvoiceSection';
import { LocationPicker, LocationBadge } from '../components/LocationPicker';
import { JobFormSubmissions } from '../components/JobFormSubmissions';
import { PrepareTruckDrawer } from '../components/PrepareTruckDrawer';
import TagMultiSelect, { TagChips } from '../components/TagMultiSelect';
import { JobStatusBadge, PaymentStatusBadge, cleanJobNumber } from '../components/JobStatusBadge';
import MapPreviewCard from '../components/MapPreviewCard';
import JobProfitabilityCard from '../components/JobProfitabilityCard';
import JobWorkflowTimeline from '../components/JobWorkflowTimeline';
import TakePaymentDrawer from '../components/TakePaymentDrawer';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet default marker icon
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'paused', label: 'Paused', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
];


/* StatusBadge removed — use shared JobStatusBadge instead */


function PrivateNotesCard({ jobId, notes: initialNotes, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(initialNotes);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setText(initialNotes); }, [initialNotes]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await crmJobsAPI.update(jobId, { notes: text });
      setEditing(false);
      toast.success('Notes saved');
    } catch { toast.error('Failed to save notes'); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="private-notes-card">
      <CardHeader className="pb-1 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <StickyNote className="w-4 h-4" /> Private Notes
          </CardTitle>
          {canEdit && !editing && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setEditing(true)} data-testid="edit-notes-btn">
              <Edit2 className="w-3 h-3 mr-1" /> Edit
            </Button>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground">Internal only — not visible to customers.</p>
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-1">
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Add private notes about this job..."
              className="min-h-[80px] text-sm"
              data-testid="private-notes-textarea"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-notes-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setText(initialNotes); setEditing(false); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className={`text-sm whitespace-pre-wrap ${text ? 'text-foreground' : 'text-muted-foreground italic'}`} data-testid="private-notes-display">
            {text || 'No notes yet.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* ---- Street View Preview with fallback ---- */
function StreetViewPreview({ address, latitude, longitude }) {
  const [streetViewFailed, setStreetViewFailed] = useState(false);
  const [staticMapFailed, setStaticMapFailed] = useState(false);
  const hasCoords = latitude && longitude;
  const query = address ? encodeURIComponent(address) : hasCoords ? `${latitude},${longitude}` : null;

  // Tier 1: Google Street View Static
  const streetViewSrc = MAPS_API_KEY && query
    ? `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${query}&key=${MAPS_API_KEY}`
    : null;

  // Tier 2: Google Static Maps
  const staticMapSrc = MAPS_API_KEY && query
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${query}&zoom=16&size=400x300&scale=2&maptype=roadmap&markers=color:red|${query}&key=${MAPS_API_KEY}`
    : null;

  // Tier 3: OpenStreetMap embed
  const osmUrl = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${(longitude - 0.008).toFixed(6)},${(latitude - 0.005).toFixed(6)},${(longitude + 0.008).toFixed(6)},${(latitude + 0.005).toFixed(6)}&layer=mapnik&marker=${latitude},${longitude}`
    : null;

  const showStreet = streetViewSrc && !streetViewFailed;
  const showStatic = !showStreet && staticMapSrc && !staticMapFailed;
  const showOsm = !showStreet && !showStatic && osmUrl;

  return (
    <div data-testid="street-view-preview" className="h-full">
      {showStreet ? (
        <img
          src={streetViewSrc}
          alt="Street View"
          className="w-full h-full object-cover"
          onError={() => setStreetViewFailed(true)}
          data-testid="street-view-img"
        />
      ) : showStatic ? (
        <img
          src={staticMapSrc}
          alt="Map preview"
          className="w-full h-full object-cover"
          onError={() => setStaticMapFailed(true)}
          data-testid="static-map-fallback-img"
        />
      ) : showOsm ? (
        <iframe
          src={osmUrl}
          className="w-full h-full pointer-events-none"
          title="Map preview"
          loading="lazy"
          data-testid="osm-fallback-embed"
        />
      ) : (
        <div className="bg-muted/30 flex items-center justify-center h-full" data-testid="streetview-fallback">
          <div className="flex flex-col items-center gap-1.5 text-muted-foreground/40">
            <MapPin className="w-5 h-5" />
            <span className="text-[10px]">Preview unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isOffice, hasEntitlement } = useAuth();
  const hasCrm = hasEntitlement('crm');
  const hcpMode = !hasCrm && hasEntitlement('hcp_jobs');
  const canEdit = (isOwner || isOffice) && !hcpMode;
  const showInventory = hasCrm || hcpMode;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [customers, setCustomers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [editCustomerLocations, setEditCustomerLocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [materialsKey, setMaterialsKey] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [kitDetail, setKitDetail] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [missingDrawerOpen, setMissingDrawerOpen] = useState(false);
  const [restockDrawerOpen, setRestockDrawerOpen] = useState(false);
  const [restockRows, setRestockRows] = useState([]);
  const [restockSaving, setRestockSaving] = useState(false);
  const [prepareTruckOpen, setPrepareTruckOpen] = useState(false);
  const [allJobTags, setAllJobTags] = useState([]);

  // OMW State
  const [omwSending, setOmwSending] = useState(false);
  const [omwConfirmOpen, setOmwConfirmOpen] = useState(false);
  const [notifLog, setNotifLog] = useState([]);
  const [omwRecentlySent, setOmwRecentlySent] = useState(false);

  // Convert to Project State
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  // Take Payment Drawer
  const [takePaymentOpen, setTakePaymentOpen] = useState(false);

  // Apply Workflow Drawer (triggered from More Actions menu)
  const [applyWorkflowOpen, setApplyWorkflowOpen] = useState(false);

  // Clock In/Out State (for workflow timeline)
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [openLogId, setOpenLogId] = useState(null);
  const [profitData, setProfitData] = useState(null);

  // Job Invoices
  const [jobInvoices, setJobInvoices] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [confirmNewInvoice, setConfirmNewInvoice] = useState(false);

  const fetchJobInvoices = useCallback(async () => {
    if (!hasCrm) return;
    try {
      const res = await invoicesAPI.forJob(id);
      setJobInvoices(res.data || []);
    } catch { /* non-critical */ }
  }, [id, hasCrm]);

  useEffect(() => { fetchJobInvoices(); }, [fetchJobInvoices]);

  const handleCreateInvoice = async () => {
    // If drafts exist, redirect to the latest draft instead of creating a new one
    const existingDraft = jobInvoices.find(i => i.status === 'draft');
    if (existingDraft && jobInvoices.length > 0 && !confirmNewInvoice) {
      navigate(`/invoices/${existingDraft.id}`);
      return;
    }
    setConfirmNewInvoice(false);
    setCreatingInvoice(true);
    try {
      const res = await invoicesAPI.fromJob(id);
      toast.success('Invoice created — opening editor');
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const fetchJob = useCallback(async () => {
    try {
      const res = await crmJobsAPI.get(id);
      setJob(res.data);
      setEditForm({
        job_number: res.data.job_number,
        job_name: res.data.job_name || '',
        description: res.data.description || '',
        amount: res.data.amount != null ? String(res.data.amount) : '',
        customer_id: res.data.customer_id || '',
        customer_location_id: res.data.customer_location_id || '',
        status: res.data.status,
        notes: res.data.notes || '',
        template_id: res.data.template_id || '',
        job_type: res.data.job_type || '',
        priority: res.data.priority || 'normal',
        selectedTags: res.data.tag_objects || [],
      });
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error('Job not found');
        navigate('/jobs');
      } else {
        toast.error('Failed to load job');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await customersAPI.list({ page_size: 100, is_active: true });
      setCustomers(res.data.customers || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchJob(); fetchCustomers(); }, [fetchJob, fetchCustomers]);

  // Fetch customer detail for property card
  useEffect(() => {
    if (!job?.customer_id) { setCustomer(null); return; }
    customersAPI.get(job.customer_id).then(r => setCustomer(r.data)).catch(() => {});
  }, [job?.customer_id]);

  useEffect(() => {
    tagsAPI.list({ entity_type: 'job' }).then(res => setAllJobTags(res.data || [])).catch(() => {});
  }, []);

  // Fetch locations for the customer currently selected in the edit form
  useEffect(() => {
    if (!editDrawerOpen || !editForm.customer_id) { setEditCustomerLocations([]); return; }
    // Use already-loaded customer if IDs match
    if (customer && customer.id === editForm.customer_id && customer.locations) {
      setEditCustomerLocations(customer.locations);
      return;
    }
    customersAPI.get(editForm.customer_id)
      .then(r => setEditCustomerLocations(r.data?.locations || []))
      .catch(() => setEditCustomerLocations([]));
  }, [editDrawerOpen, editForm.customer_id, customer]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await jobTemplatesAPI.list();
      setTemplates(res.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const fetchKitDetail = useCallback(async () => {
    if (!job?.kit_id) { setKitDetail(null); return; }
    try {
      const res = await jobKitsAPI.get(job.kit_id);
      setKitDetail(res.data);
    } catch { setKitDetail(null); }
  }, [job?.kit_id]);

  useEffect(() => { fetchKitDetail(); }, [fetchKitDetail]);

  // Fetch notification log for OMW state
  const fetchNotifLog = useCallback(async () => {
    if (!id) return;
    try {
      const res = await crmJobsAPI.notificationLog(id);
      setNotifLog(res.data || []);
      const recentOmw = (res.data || []).find(l =>
        l.notification_type === 'omw' && l.status === 'sent' &&
        new Date(l.created_at) > new Date(Date.now() - 10 * 60 * 1000)
      );
      setOmwRecentlySent(!!recentOmw);
    } catch { /* non-critical */ }
  }, [id]);

  useEffect(() => { fetchNotifLog(); }, [fetchNotifLog]);

  // Fetch clock-in status for workflow timeline
  const fetchClockStatus = useCallback(async () => {
    if (!hasCrm) return;
    try {
      const logs = await crmJobsAPI.timeLogs(id);
      const open = (logs.data || []).find(l => !l.clock_out);
      setOpenLogId(open?.id || null);
    } catch { /* non-critical */ }
  }, [id, hasCrm]);

  useEffect(() => { fetchClockStatus(); }, [fetchClockStatus]);

  const handleTimelineClockIn = async () => {
    setClockingIn(true);
    try {
      await crmJobsAPI.clockIn(id);
      toast.success('Clocked in');
      fetchClockStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  const handleTimelineClockOut = async () => {
    setClockingOut(true);
    try {
      await crmJobsAPI.clockOut(id);
      toast.success('Clocked out');
      fetchClockStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  const handleSendOMW = async () => {
    setOmwConfirmOpen(false);
    setOmwSending(true);
    try {
      const res = await crmJobsAPI.sendOMW(id);
      if (res.data.sent) {
        toast.success(`On My Way notification sent via ${res.data.channel}`);
        setOmwRecentlySent(true);
        fetchNotifLog();
      } else {
        toast.error(res.data.reason || 'Could not send notification');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OMW notification');
    } finally {
      setOmwSending(false);
    }
  };

  const fetchReadiness = useCallback(async () => {
    if (!job?.kit_id) { setReadiness(null); return; }
    setReadinessLoading(true);
    try {
      const res = await crmJobsAPI.readiness(id);
      setReadiness(res.data);
    } catch { setReadiness(null); }
    finally { setReadinessLoading(false); }
  }, [id, job?.kit_id]);

  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  // Stage 15: Generate Restock Plan from readiness missing items
  const openRestockPlan = useCallback(() => {
    if (!readiness) return;
    const missing = readiness.items.filter(i => i.status === 'missing' || i.status === 'partial');
    setRestockRows(missing.map(i => ({
      item_id: i.item_id,
      item_name: i.item_name,
      sku: i.sku,
      quantity_required: i.quantity_required,
      quantity_on_van: i.quantity_on_van,
      needed: Math.max(0, i.quantity_required - i.quantity_available),
      editQty: Math.max(0, i.quantity_required - i.quantity_available),
    })));
    setMissingDrawerOpen(false);
    setRestockDrawerOpen(true);
  }, [readiness]);

  const updateRestockQty = (itemId, value) => {
    setRestockRows(prev => prev.map(r =>
      r.item_id === itemId ? { ...r, editQty: Math.max(0, parseInt(value) || 0) } : r
    ));
  };

  const removeRestockRow = (itemId) => {
    setRestockRows(prev => prev.filter(r => r.item_id !== itemId));
  };

  const handleSaveRestock = async () => {
    const valid = restockRows.filter(r => r.editQty > 0);
    if (!valid.length) { toast.error('No items to save'); return; }
    setRestockSaving(true);
    try {
      const res = await predictiveRestockAPI.savePlan({
        name: `Restock for ${job.job_number} — ${readiness?.vehicle_name || 'Vehicle'}`,
        items: valid.map(r => ({
          item_id: r.item_id,
          source_type: 'vehicle',
          source_id: readiness.vehicle_id,
          suggested_qty: r.editQty,
        })),
      });
      toast.success(`Restock list created with ${res.data.total_items} items`);
      setRestockDrawerOpen(false);
      navigate('/replenishment');
    } catch {
      toast.error('Failed to save restock list');
    } finally {
      setRestockSaving(false);
    }
  };

  const fetchSummary = useCallback(async () => {
    if (!hasCrm) return;
    try {
      const res = await jobMaterialsAPI.summary(id);
      setSummary(res.data);
    } catch { /* non-critical */ }
  }, [id, hasCrm]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleMaterialsChanged = () => {
    setMaterialsKey(k => k + 1);
    fetchSummary();
    fetchReadiness(); // Stage 16: recalculate readiness after material deduction
  };

  const handleCreateTag = async (name, entityType) => {
    try {
      const res = await tagsAPI.create({ name, entity_type: entityType });
      const newTag = res.data;
      setAllJobTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      return newTag;
    } catch { return null; }
  };

  const openEditDrawer = () => {
    setEditForm({
      job_number: job.job_number,
      job_name: job.job_name || '',
      description: job.description || '',
      amount: job.amount != null ? String(job.amount) : '',
      customer_id: job.customer_id || '',
      customer_location_id: job.customer_location_id || '',
      status: job.status,
      notes: job.notes || '',
      template_id: job.template_id || '',
      job_type: job.job_type || '',
      priority: job.priority || 'normal',
      selectedTags: job.tag_objects || [],
    });
    setEditDrawerOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editForm.job_number?.trim()) { toast.error('Job number is required'); return; }
    setSaving(true);
    try {
      await crmJobsAPI.update(id, {
        job_number: editForm.job_number.trim(),
        job_name: editForm.job_name.trim() || null,
        description: editForm.description.trim() || null,
        amount: editForm.amount ? parseFloat(editForm.amount) : null,
        customer_id: editForm.customer_id || null,
        customer_location_id: editForm.customer_location_id || null,
        status: editForm.status,
        notes: editForm.notes.trim() || null,
        template_id: editForm.template_id || null,
        job_type: editForm.job_type.trim() || null,
        priority: editForm.priority || 'normal',
        tag_ids: (editForm.selectedTags || []).map(t => t.id),
      });
      toast.success('Job updated');
      setEditDrawerOpen(false);
      fetchJob();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await crmJobsAPI.remove(id);
      toast.success('Job deleted');
      navigate('/jobs');
    } catch {
      toast.error('Failed to delete job');
    }
  };

  const handleQuickStatus = async (newStatus) => {
    try {
      await crmJobsAPI.update(id, { status: newStatus });
      toast.success(`Status updated to ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`);
      fetchJob();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleConvertToProject = async () => {
    setConverting(true);
    try {
      await projectsAPI.convertToProject(id);
      toast.success('Job converted to project');
      navigate(`/jobs/projects/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to convert to project');
    } finally {
      setConverting(false);
      setConvertConfirmOpen(false);
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div></Layout>;
  }
  if (!job) return null;

  const createdDate = job.created_at ? formatInTenantTimezone(job.created_at, { showTimezone: false, dateStyle: 'medium', timeStyle: 'short' }) : '';

  return (
    <Layout>
      <div className="space-y-6" data-testid="job-detail-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')} data-testid="back-to-jobs">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        {/* Row 1: Job Summary + Customer & Property (50/50) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Job Summary Card */}
          <Card data-testid="job-info-card">
            <CardHeader className="pb-1.5 pt-3 px-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary shrink-0">
                  <Briefcase className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <CardTitle className="text-sm font-semibold truncate" data-testid="job-title">
                      {job.customer_name ? `Job for ${job.customer_name}` : `Job ${cleanJobNumber(job.job_number)}`}
                    </CardTitle>
                    {canEdit && (
                      <button onClick={openEditDrawer} className="inline-flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors" title="Edit" data-testid="edit-job-btn">
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => setDeleteConfirm(true)} className="inline-flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 transition-colors" title="Delete" data-testid="delete-job-btn">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-muted-foreground font-medium" data-testid="job-number">#{cleanJobNumber(job.job_number)}</span>
                    <span className="text-muted-foreground/40">·</span>
                    <JobStatusBadge status={job.status} />
                    {job.source_system === 'housecallpro' && (
                      <><span className="text-muted-foreground/40">·</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800" data-testid="job-detail-hcp-badge">
                        <ExternalLink className="w-3 h-3" /> Housecall Pro
                        {job.source_id && <span className="font-normal text-amber-700 dark:text-amber-500">#{job.source_id}</span>}
                      </span></>
                    )}
                    {job.payment_status && (<><span className="text-muted-foreground/40">·</span><PaymentStatusBadge status={job.payment_status} /></>)}
                    {createdDate && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" /> {createdDate}</span>}
                  </div>
                </div>
                {canEdit && showInventory && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button onClick={() => setQuickAddOpen(true)} className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors shrink-0" data-testid="quick-add-material-btn">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Quick Add Material</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 px-4 pb-3 pt-0">
              {job.job_name && <div className="text-sm"><span className="text-muted-foreground">Job Name:</span> <span className="font-medium ml-1">{job.job_name}</span></div>}
              {job.description && <div className="text-sm"><span className="text-muted-foreground">Description:</span> <p className="mt-0.5 whitespace-pre-wrap">{job.description}</p></div>}
              {job.amount != null && <div className="text-sm"><span className="text-muted-foreground">Amount:</span> <span className="font-semibold ml-1">${parseFloat(job.amount).toFixed(2)}</span></div>}
              {job.job_type && (
                <div data-testid="job-detail-type" className="text-sm">
                  <span className="text-muted-foreground">Job Type:</span>
                  <span className={`inline-flex items-center ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                    job.job_type === 'maintenance' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' :
                    'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400'
                  }`}>{job.job_type.charAt(0).toUpperCase() + job.job_type.slice(1)}</span>
                </div>
              )}
              {job.priority && job.priority !== 'normal' && (
                <div data-testid="job-detail-priority" className="text-sm">
                  <span className="text-muted-foreground">Priority:</span>
                  <span className={`inline-flex items-center ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                    job.priority === 'urgent' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                    job.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                    job.priority === 'low' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
                    'bg-blue-100 text-blue-800'
                  }`}>{job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}</span>
                </div>
              )}
              {job.tag_objects && job.tag_objects.length > 0 && (
                <div data-testid="job-detail-tags" className="text-sm">
                  <span className="text-muted-foreground">Tags:</span>
                  <span className="ml-1.5"><TagChips tags={job.tag_objects} /></span>
                </div>
              )}
              {job.completed_at && <div data-testid="job-detail-completed" className="text-sm"><span className="text-muted-foreground">Completed:</span> <span className="ml-1">{formatInTenantTimezone(job.completed_at, { showTimezone: false, dateStyle: 'medium', timeStyle: 'short' })}</span></div>}
              {job.template_name && (
                <div className="flex items-center gap-1.5 text-sm">
                  <FileStack className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Template:</span>
                  <span className="font-medium">{job.template_name}</span>
                </div>
              )}

              {canEdit && (
                <div className="pt-1.5 border-t border-border">
                  <div className="flex items-center gap-2">
                    {/* Quick Status Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" data-testid="quick-status-dropdown">
                          <JobStatusBadge status={job.status} />
                          <ChevronDown className="w-3 h-3 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuLabel className="text-xs">Change Status</DropdownMenuLabel>
                        {STATUS_OPTIONS.filter((s) => s.value !== job.status).map((s) => (
                          <DropdownMenuItem key={s.value} onClick={() => handleQuickStatus(s.value)} data-testid={`quick-status-${s.value}`}>
                            {s.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* More Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground" data-testid="job-more-actions">
                          <MoreHorizontal className="w-3.5 h-3.5" /> More
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => setApplyWorkflowOpen(true)} data-testid="menu-apply-workflow">
                          <Zap className="w-3.5 h-3.5 mr-2" /> Apply Workflow
                        </DropdownMenuItem>
                        {(!job.job_mode || job.job_mode === 'service') && (
                          <DropdownMenuItem onClick={() => setConvertConfirmOpen(true)} data-testid="menu-convert-project">
                            <FolderKanban className="w-3.5 h-3.5 mr-2" /> Convert to Project
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )}

              {/* Workflow Actions — primary action row */}
              {!hcpMode && canEdit && (
                <JobWorkflowTimeline
                  hasAppointment={!!(job.scheduled_start || job.dispatch_events?.length > 0)}
                  omwRecentlySent={omwRecentlySent}
                  omwSending={omwSending}
                  openLogId={openLogId}
                  clockingIn={clockingIn}
                  clockingOut={clockingOut}
                  hasInvoices={jobInvoices.length > 0}
                  hasPaidInvoice={jobInvoices.some(i => i.status === 'paid')}
                  hasBalanceDue={jobInvoices.some(i => i.status !== 'void' && i.status !== 'paid' && Number(i.balance_due || i.total || 0) > 0)}
                  onAppointment={() => navigate(`/dispatch?job_id=${id}`)}
                  onOmw={() => setOmwConfirmOpen(true)}
                  onClockIn={handleTimelineClockIn}
                  onClockOut={handleTimelineClockOut}
                  onGenerateInvoice={handleCreateInvoice}
                  onTakePayment={() => setTakePaymentOpen(true)}
                  canEdit={canEdit}
                  hcpMode={hcpMode}
                  hasCustomer={!!job.customer_id}
                />
              )}
            </CardContent>
          </Card>
          {customer ? (
            <>
            <Card data-testid="customer-property-card" className="overflow-hidden h-full">
              <div className="flex flex-col md:flex-row h-full">
                {/* Left: contact + address */}
                <div className="p-4 md:w-[45%] space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-1">
                    <Building2 className="w-3.5 h-3.5" /> Customer & Property
                  </p>
                  <button
                    className="font-medium text-sm text-primary hover:underline text-left"
                    onClick={() => navigate(`/customers/${job.customer_id}`)}
                    data-testid="customer-name-link"
                  >
                    {customer.display_name}
                  </button>
                  {job.customer_location ? (
                    <LocationBadge location={job.customer_location} />
                  ) : (customer.service_street || customer.service_city) && (
                    <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{[customer.service_street, customer.service_city, customer.service_state, customer.service_zip].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {(customer.primary_phone || customer.billing_phone) && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Phone className="w-3.5 h-3.5" />
                      <span>{customer.primary_phone || customer.billing_phone}</span>
                    </div>
                  )}
                  {customer.billing_email && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Mail className="w-3.5 h-3.5" />
                      <span className="truncate">{customer.billing_email}</span>
                    </div>
                  )}
                  {customer.property_profile && (() => {
                    const p = customer.property_profile;
                    const items = [
                      p.bedrooms != null && `${p.bedrooms} bed`,
                      p.bathrooms != null && `${p.bathrooms} bath`,
                      p.square_footage && `${Number(p.square_footage).toLocaleString()} sq ft`,
                      p.year_built && `Built ${p.year_built}`,
                    ].filter(Boolean);
                    return items.length > 0 ? (
                      <p className="text-xs text-muted-foreground mt-1 pt-2 border-t" data-testid="job-property-summary">{items.join(' · ')}</p>
                    ) : null;
                  })()}
                </div>

                {/* Right: Street View image */}
                <div className="md:flex-1 min-h-[180px]">
                  <StreetViewPreview
                    address={job.customer_location
                      ? [job.customer_location.address1, job.customer_location.city, job.customer_location.state, job.customer_location.zip].filter(Boolean).join(', ')
                      : [customer.service_street, customer.service_city, customer.service_state, customer.service_zip].filter(Boolean).join(', ')}
                    latitude={job.customer_location ? null : customer.latitude}
                    longitude={job.customer_location ? null : customer.longitude}
                  />
                </div>
              </div>
            </Card>
          {/* Priority Customer Notes Banner */}
          {(customer.service_address_notes || customer.notes) && (
            <div data-testid="customer-priority-notes" className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Priority Notes
              </p>
              {customer.service_address_notes && (
                <p className="text-sm text-amber-900 dark:text-amber-200" data-testid="service-address-notes">
                  {customer.service_address_notes}
                </p>
              )}
              {customer.notes && (
                <p className="text-sm text-amber-800/80 dark:text-amber-300/80" data-testid="customer-general-notes">
                  {customer.notes}
                </p>
              )}
            </div>
          )}
            </>
          ) : (
            <Card data-testid="customer-property-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Customer & Property
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground italic">No customer assigned.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(false); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Job</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete Job #{cleanJobNumber(job.job_number)}. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-job">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-job"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* OMW Confirmation Dialog */}
        <AlertDialog open={omwConfirmOpen} onOpenChange={setOmwConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" /> Send "On My Way"
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will notify the customer that a technician is on the way to their appointment.
                {job?.customer_name && <span className="block mt-1 font-medium text-foreground">Customer: {job.customer_name}</span>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-omw">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleSendOMW} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-omw">
                Send Notification
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Convert to Project Confirmation */}
        <AlertDialog open={convertConfirmOpen} onOpenChange={setConvertConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-primary" /> Convert to Project
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will convert Job <span className="font-medium">#{cleanJobNumber(job.job_number)}</span> into a Project Job with a full workspace (budget, tasks, daily logs, files, and activity tracking).
                <span className="block mt-2 text-foreground/80">All existing job data (customer, materials, invoices, notes) will be preserved. This action can&apos;t be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-convert-project">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConvertToProject} disabled={converting} data-testid="confirm-convert-project">
                {converting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FolderKanban className="w-4 h-4 mr-2" />}
                Convert to Project
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Private Notes — CRM only */}
        {!hcpMode && <PrivateNotesCard jobId={job.id} notes={job.notes || ''} canEdit={canEdit} />}

        {/* Custom Fields — CRM only */}
        {!hcpMode && <JobCustomFields jobId={job.id} canEdit={canEdit} />}

        {/* Cost Rollup (Stage 4.5B — CRM gated) */}
        {showInventory && summary && summary.total_parts_count > 0 && (
          <Card data-testid="job-cost-rollup">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Parts cost:</span>
                  <span className="font-semibold" data-testid="rollup-total-cost">${summary.total_parts_cost.toFixed(2)}</span>
                </div>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground" data-testid="rollup-parts-count">{summary.total_parts_count} items ({summary.total_items_count} line{summary.total_items_count !== 1 ? 's' : ''})</span>
                {summary.by_source_type?.vehicle?.count > 0 && (
                  <span className="text-xs text-muted-foreground">Van: {summary.by_source_type.vehicle.count}</span>
                )}
                {summary.by_source_type?.warehouse?.count > 0 && (
                  <span className="text-xs text-muted-foreground">Warehouse: {summary.by_source_type.warehouse.count}</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Job Profitability Card (Labor Costing — CRM gated) */}
        {hasCrm && <JobProfitabilityCard jobId={id} />}

        {/* Stage 14B: Van Readiness Indicator */}
        {showInventory && readiness && readiness.status !== 'no_kit' && (
          <Card data-testid="van-readiness-card">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TooltipProvider>
                  {readiness.status === 'ready' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 cursor-help">
                          <ShieldCheck className="w-5 h-5" />
                          <span className="font-medium text-sm">Van Ready</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>The assigned vehicle has all parts required for this job.</TooltipContent>
                    </Tooltip>
                  )}
                  {readiness.status === 'missing_items' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 cursor-help">
                          <CircleAlert className="w-5 h-5" />
                          <span className="font-medium text-sm">{readiness.status_label}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>The vehicle is missing one or more parts needed for this job.</TooltipContent>
                    </Tooltip>
                  )}
                  {readiness.status === 'low_stock' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 cursor-help">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-medium text-sm">Low Stock Warning</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Parts are available but stock will drop below minimum after use.</TooltipContent>
                    </Tooltip>
                  )}
                  {readiness.status === 'no_vehicle' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 text-muted-foreground cursor-help">
                          <Truck className="w-5 h-5" />
                          <span className="font-medium text-sm">No Vehicle Assigned</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>No technician with a vehicle is assigned to this job yet.</TooltipContent>
                    </Tooltip>
                  )}
                  </TooltipProvider>
                  {readiness.vehicle_name && (
                    <span className="text-xs text-muted-foreground">{readiness.vehicle_name}</span>
                  )}
                  {readiness.assigned_user_name && (
                    <span className="text-xs text-muted-foreground">({readiness.assigned_user_name})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {readiness.ready_count}/{readiness.total_items} ready
                  </span>
                  {/* Stage 17A: Prepare Truck Button */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="default" size="sm" onClick={() => setPrepareTruckOpen(true)} data-testid="prepare-truck-btn">
                          <Truck className="w-3 h-3 mr-1" /> Prepare Truck
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Prepare the assigned vehicle with all parts required for this job.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {(readiness.status === 'missing_items' || readiness.status === 'low_stock' || readiness.status === 'no_vehicle') && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="sm" onClick={() => setMissingDrawerOpen(true)} data-testid="view-missing-parts-btn">
                            View Details
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>See which parts are missing or low and take action.</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stage 13C: Job Kit Section */}
        {showInventory && kitDetail && (
          <Card data-testid="job-kit-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-base">Job Kit</CardTitle>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    kitDetail.status === 'closed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}>
                    {kitDetail.status}
                  </span>
                </div>
                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={() => navigate('/job-kits')} data-testid="open-job-kit-btn">
                    <ExternalLink className="w-3 h-3 mr-1" /> View Kit
                  </Button>
                </TooltipTrigger><TooltipContent>Open the full Job Kit management page.</TooltipContent></Tooltip></TooltipProvider>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{kitDetail.name}</p>
              {kitDetail.part_requests && kitDetail.part_requests.length > 0 ? (
                <div className="space-y-2">
                  {kitDetail.part_requests.map((pr) => (
                    <div key={pr.id} className="border border-border rounded-md p-3" data-testid={`kit-part-request-${pr.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">
                          {pr.warehouse_name || 'Part Request'}
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          pr.status === 'Fulfilled' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                          pr.status === 'Picked' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' :
                          'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {pr.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{pr.line_item_count} item{pr.line_item_count !== 1 ? 's' : ''}</span>
                        {pr.total_estimated_cost > 0 && (
                          <span>Est. ${pr.total_estimated_cost.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No part requests in this kit yet.</p>
              )}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t text-xs text-muted-foreground">
                <span>Requests: {kitDetail.request_count}</span>
                <span>Pending: {kitDetail.pending_count}</span>
                <span>Fulfilled: {kitDetail.fulfilled_count}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoice Quick Actions (high visibility) */}
        {hasCrm && (
          <Card data-testid="job-invoice-actions-card">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 shrink-0">
                    <Receipt className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">Invoicing</p>
                    {jobInvoices.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No invoices yet — generate one from this job's materials</p>
                    ) : (
                      <div className="flex flex-col gap-1 mt-1">
                        {jobInvoices.map(inv => (
                          <button
                            key={inv.id}
                            onClick={() => navigate(`/invoices/${inv.id}`)}
                            className="inline-flex items-center gap-2 text-xs hover:bg-muted rounded px-1.5 py-0.5 transition-colors text-left w-fit"
                            data-testid={`job-invoice-chip-${inv.id}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              inv.status === 'paid' ? 'bg-green-500' :
                              inv.status === 'sent' ? 'bg-blue-500' :
                              inv.status === 'void' ? 'bg-red-500' :
                              'bg-amber-500'
                            }`} />
                            <span className="font-medium">{inv.invoice_number}</span>
                            <span className="text-muted-foreground capitalize">{inv.status}</span>
                            <span className="text-muted-foreground">${Number(inv.total || 0).toFixed(2)}</span>
                            {inv.created_at && <span className="text-muted-foreground">{new Date(inv.created_at).toLocaleDateString()}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Primary: if a draft exists, go to it. Otherwise generate. */}
                  {jobInvoices.some(i => i.status === 'draft') ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        const draft = jobInvoices.find(i => i.status === 'draft');
                        if (draft) navigate(`/invoices/${draft.id}`);
                      }}
                      data-testid="edit-draft-invoice-btn"
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit Draft
                    </Button>
                  ) : canEdit && (
                    <Button
                      size="sm"
                      onClick={handleCreateInvoice}
                      disabled={creatingInvoice}
                      data-testid="create-invoice-btn"
                    >
                      {creatingInvoice ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Receipt className="w-3.5 h-3.5 mr-1" />}
                      Generate Invoice
                    </Button>
                  )}
                  {/* Secondary: create another (only when invoices already exist) */}
                  {canEdit && jobInvoices.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmNewInvoice(true)}
                      data-testid="new-additional-invoice-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> New
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>

            {/* Confirmation dialog for additional invoice */}
            {confirmNewInvoice && (
              <div className="border-t px-4 py-3 bg-muted/30">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    This job already has {jobInvoices.length} invoice{jobInvoices.length !== 1 ? 's' : ''}. Create an additional invoice?
                  </p>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setConfirmNewInvoice(false)} data-testid="cancel-new-invoice-btn">Cancel</Button>
                    <Button size="sm" onClick={handleCreateInvoice} disabled={creatingInvoice} data-testid="confirm-new-invoice-btn">
                      {creatingInvoice ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                      Create Invoice
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Materials Tab (Stage 4 — Inventory accessible) */}
        {showInventory && (
          <Card data-testid="job-materials-card">
            <CardHeader className="pb-1 pt-3 px-4">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm">Materials</CardTitle>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help text-muted-foreground" data-testid="materials-auto-deduct-tooltip">
                        <AlertTriangle className="w-3.5 h-3.5" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Materials recorded here are auto-deducted from the assigned vehicle's inventory. If no vehicle is assigned, inventory is not deducted. Shortages are flagged but still recorded.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent>
              <JobMaterialsTab jobId={id} key={materialsKey} onChanged={handleMaterialsChanged} />
            </CardContent>
          </Card>
        )}

        {/* Invoice Section (Stage 7 — CRM gated) */}
        {hasCrm && (
          <Card data-testid="job-invoice-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-base">Invoice</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <JobInvoiceSection jobId={id} />
            </CardContent>
          </Card>
        )}

        {/* Photos & Attachments Section */}
        {hasCrm && <JobPhotosSection jobId={id} />}

        {/* Forms Section */}
        {hasCrm && (
          <Card>
            <CardContent className="pt-6">
              <JobFormSubmissions jobId={id} readOnly={job.status === 'completed' || job.status === 'cancelled'} />
            </CardContent>
          </Card>
        )}

        {/* Edit Job Drawer */}
        <AppDrawer
          open={editDrawerOpen}
          onOpenChange={setEditDrawerOpen}
          title="Edit Job"
          description={`Update details for ${job.job_number}`}
          data-testid="edit-job-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setEditDrawerOpen(false)}>Cancel</Button>
              <Button type="submit" form="edit-job-form" disabled={saving} data-testid="save-job-edit">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </>
          }
        >
          <form id="edit-job-form" onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Job Number *</Label>
                <Input value={editForm.job_number} onChange={(e) => setEditForm({ ...editForm, job_number: e.target.value })} data-testid="edit-job-number" />
              </div>
              <div>
                <Label>Job Name</Label>
                <Input value={editForm.job_name} onChange={(e) => setEditForm({ ...editForm, job_name: e.target.value })} data-testid="edit-job-name" />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="edit-job-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer</Label>
              <Select value={editForm.customer_id || 'none'} onValueChange={(v) => {
                const newCustId = v === 'none' ? '' : v;
                setEditForm(f => ({ ...f, customer_id: newCustId, customer_location_id: newCustId !== f.customer_id ? '' : f.customer_location_id }));
              }}>
                <SelectTrigger data-testid="edit-job-customer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No customer</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <LocationPicker
              locations={editCustomerLocations}
              value={editForm.customer_location_id}
              onChange={(locId) => setEditForm(f => ({ ...f, customer_location_id: locId || '' }))}
            />
            <div>
              <Label>Job Template</Label>
              {!job.kit_id && <p className="text-xs text-muted-foreground mb-1">Select a template to auto-generate a Job Kit</p>}
              {job.kit_id && <p className="text-xs text-muted-foreground mb-1">Kit already generated — changing template updates reference only</p>}
              <Select value={editForm.template_id || 'none'} onValueChange={(v) => setEditForm({ ...editForm, template_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="edit-job-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.item_count} items)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Description</Label>
                <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2} placeholder="Scope of work..." data-testid="edit-job-description" />
              </div>
              <div>
                <Label>Amount ($)</Label>
                <Input type="number" step="0.01" min="0" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} placeholder="0.00" data-testid="edit-job-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Job Type</Label>
                <Input value={editForm.job_type} onChange={(e) => setEditForm({ ...editForm, job_type: e.target.value })} placeholder="e.g. Install, Repair" data-testid="edit-job-type" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={editForm.priority || 'normal'} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger data-testid="edit-job-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Tags</Label>
              <TagMultiSelect
                entityType="job"
                selectedTags={editForm.selectedTags || []}
                onChange={(tags) => setEditForm({ ...editForm, selectedTags: tags })}
                allTags={allJobTags}
                onCreateTag={handleCreateTag}
                data-testid="edit-job-tags"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} data-testid="edit-job-notes" />
            </div>
          </form>
        </AppDrawer>

        {/* Quick Add Material Modal (Stage 4.5A) */}
        {quickAddOpen && (
          <QuickAddMaterialModal jobId={id} open={quickAddOpen} onClose={() => setQuickAddOpen(false)} onAdded={handleMaterialsChanged} />
        )}

        {/* Stage 14C: Missing Parts Drawer */}
        <AppDrawer
          open={missingDrawerOpen}
          onClose={() => setMissingDrawerOpen(false)}
          title="Van Readiness Details"
          description={readiness ? `${readiness.vehicle_name || 'No vehicle'} — ${readiness.status_label}` : ''}
        >
          {readiness && (
            <div className="space-y-4" data-testid="missing-parts-drawer">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md border p-2">
                  <div className="text-lg font-bold text-green-600">{readiness.ready_count}</div>
                  <div className="text-xs text-muted-foreground">Ready</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-lg font-bold text-red-600">{readiness.missing_count}</div>
                  <div className="text-xs text-muted-foreground">Missing</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-lg font-bold text-amber-600">{readiness.low_stock_count}</div>
                  <div className="text-xs text-muted-foreground">Low Stock</div>
                </div>
              </div>

              <div className="space-y-2">
                {readiness.items.map((item, idx) => (
                  <div
                    key={item.item_id || idx}
                    data-testid={`readiness-item-${item.item_id}`}
                    className={`border rounded-md p-3 ${
                      item.status === 'missing' ? 'border-red-200 bg-red-50 dark:border-red-900/30 dark:bg-red-950/20' :
                      item.status === 'partial' ? 'border-orange-200 bg-orange-50 dark:border-orange-900/30 dark:bg-orange-950/20' :
                      item.status === 'low_stock' ? 'border-amber-200 bg-amber-50 dark:border-amber-900/30 dark:bg-amber-950/20' :
                      'border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-950/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{item.item_name}</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        item.status === 'ok' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400' :
                        item.status === 'missing' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400' :
                        item.status === 'partial' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-400' :
                        'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400'
                      }`}>
                        {item.status === 'ok' ? 'Ready' : item.status === 'partial' ? 'Partial' : item.status === 'low_stock' ? 'Low' : 'Missing'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {item.sku && <span>SKU: {item.sku}</span>}
                      <span>Required: {item.quantity_required}</span>
                      <span>On Van: {item.quantity_on_van}</span>
                      <span>Available: {item.quantity_available}</span>
                      {item.min_level != null && <span>Min: {item.min_level}</span>}
                    </div>
                    {(item.status === 'missing' || item.status === 'partial') && (
                      <div className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">
                        Need {item.quantity_required - item.quantity_available} more
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {readiness.status === 'missing_items' && readiness.vehicle_id && (
                <div className="pt-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="w-full"
                          size="sm"
                          onClick={openRestockPlan}
                          data-testid="generate-restock-btn"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Generate Restock Plan
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Create a restock list for the missing items on this vehicle.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}

              {(readiness.status === 'missing_items' || readiness.status === 'no_vehicle') && (
                <div className="flex gap-2 pt-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="flex-1"
                          variant="outline"
                          size="sm"
                          onClick={() => { setMissingDrawerOpen(false); navigate('/fleet'); }}
                          data-testid="go-fleet-btn"
                        >
                          <Truck className="w-3 h-3 mr-1" /> Fleet Health
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>View all vehicles and their inventory status.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="flex-1"
                          variant="outline"
                          size="sm"
                          onClick={() => { setMissingDrawerOpen(false); navigate('/job-kits'); }}
                          data-testid="go-job-kits-btn"
                        >
                          <Package className="w-3 h-3 mr-1" /> Job Kits
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Manage Job Kits and Part Requests.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          )}
        </AppDrawer>

        {/* Stage 15: Restock Review Drawer */}
        <AppDrawer
          open={restockDrawerOpen}
          onClose={() => setRestockDrawerOpen(false)}
          title="Review Restock Plan"
          description={`${readiness?.vehicle_name || 'Vehicle'} — ${job?.job_number || ''}`}
        >
          <div className="space-y-4" data-testid="restock-review-drawer">
            <p className="text-sm text-muted-foreground">
              Review and adjust quantities before saving. This creates a replenishment list
              for transfer from warehouse to the vehicle.
            </p>

            {restockRows.length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right w-20">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="cursor-help underline decoration-dotted">Need</TooltipTrigger>
                            <TooltipContent>Quantity needed to meet job requirements.</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableHead>
                      <TableHead className="text-right w-28">Restock Qty</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {restockRows.map((row) => (
                      <TableRow key={row.item_id} data-testid={`restock-row-${row.item_id}`}>
                        <TableCell>
                          <div className="text-sm font-medium">{row.item_name}</div>
                          {row.sku && <div className="text-xs text-muted-foreground">{row.sku}</div>}
                        </TableCell>
                        <TableCell className="text-right text-sm">{row.needed}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min={0}
                            value={row.editQty}
                            onChange={(e) => updateRestockQty(row.item_id, e.target.value)}
                            className="w-20 h-8 text-right text-sm ml-auto"
                            data-testid={`restock-qty-${row.item_id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeRestockRow(row.item_id)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Remove this item from the restock plan.</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No items in the restock plan.</p>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setRestockDrawerOpen(false); setMissingDrawerOpen(true); }}
              >
                Back
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="flex-1"
                      onClick={handleSaveRestock}
                      disabled={restockSaving || !restockRows.some(r => r.editQty > 0)}
                      data-testid="save-restock-btn"
                    >
                      {restockSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                      Save Restock List
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Save this plan as a replenishment list for warehouse-to-vehicle transfer.</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </AppDrawer>

        {/* Stage 17: Prepare Truck Drawer */}
        <PrepareTruckDrawer
          open={prepareTruckOpen}
          onClose={() => setPrepareTruckOpen(false)}
          jobId={id}
          onRestocked={fetchReadiness}
        />

        {/* Take Payment Drawer */}
        <TakePaymentDrawer
          open={takePaymentOpen}
          onClose={() => setTakePaymentOpen(false)}
          invoices={jobInvoices}
          jobId={id}
          customer={customer}
          onPaymentRecorded={() => { fetchJobInvoices(); fetchJob(); }}
        />

        {/* Apply Workflow Drawer (triggered from More Actions menu) */}
        {canEdit && (
          <ApplyWorkflowButton
            jobId={job.id}
            jobMode={job.job_mode || 'service'}
            onApplied={fetchJob}
            hideButton
            externalOpen={applyWorkflowOpen}
            onExternalClose={() => setApplyWorkflowOpen(false)}
          />
        )}
      </div>
    </Layout>
  );
}


/* ── Job Photos Section ─────────────────────────────────── */
function JobPhotosSection({ jobId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = React.useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await jobAttachmentsAPI.list(jobId);
      setPhotos(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [jobId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        await jobAttachmentsAPI.upload(jobId, file);
        ok++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err?.response?.data?.detail || 'Unknown error'}`);
      }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? 's' : ''} uploaded`);
    fetchPhotos();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (att) => {
    if (!confirm(`Delete "${att.file_name}"?`)) return;
    try {
      await jobAttachmentsAPI.remove(jobId, att.id);
      toast.success('Photo deleted');
      fetchPhotos();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <Card data-testid="job-photos-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base">Photos & Files</CardTitle>
            {photos.length > 0 && (
              <span className="text-xs text-muted-foreground ml-1">({photos.length})</span>
            )}
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
              multiple
              className="hidden"
              onChange={handleUpload}
              data-testid="photo-file-input"
            />
            <Button
              size="sm" variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-photo-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
              {uploading ? 'Uploading...' : 'Add Photo'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : photos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No photos yet. Add field photos, before/after shots, or permits.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photo-grid">
            {photos.map((att) => (
              <div key={att.id} className="group relative rounded-lg overflow-hidden border bg-muted/30 aspect-square" data-testid={`photo-item-${att.id}`}>
                {att.file_type === 'image' ? (
                  <img
                    src={att.file_url.startsWith('http') ? att.file_url : `${API_URL}${att.file_url}`}
                    alt={att.file_name}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => setLightbox(att)}
                    loading="lazy"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-2 cursor-pointer" onClick={() => window.open(att.file_url.startsWith('http') ? att.file_url : `${API_URL}${att.file_url}`, '_blank')}>
                    <FileText className="w-8 h-8 text-muted-foreground mb-1" />
                    <span className="text-[10px] text-muted-foreground truncate w-full text-center">{att.file_name}</span>
                  </div>
                )}
                <button
                  onClick={() => handleDelete(att)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-photo-${att.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
                {att.uploaded_by_name && (
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 text-white text-[9px] opacity-0 group-hover:opacity-100 transition-opacity">
                    {att.uploaded_by_name}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        {lightbox && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
            <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <img
              src={lightbox.file_url.startsWith('http') ? lightbox.file_url : `${API_URL}${lightbox.file_url}`}
              alt={lightbox.file_name}
              className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
